import { getReportFooterText } from '../reportFooter';

export interface ParentNsnSizeCount {
  size: string;
  count: number;
}

export interface ParentNsnSizeGroup {
  parentCatalogId: string;
  nsn: string | null;
  displayName: string;
  sizeCounts: ParentNsnSizeCount[];
}

export interface TotalClothingSizeByWorkforceCategoryTemplateOptions {
  generatedAt: Date;
  workforceCategory: string;
  section?: string | null;
  groups: ParentNsnSizeGroup[];
}

const escapeHtml = (value: string): string =>
  value
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;')
    .replace(/'/g, '&#39;');

const sanitizeText = (value: string | null | undefined): string => {
  const trimmed = value?.trim();
  if (!trimmed) {
    return '—';
  }
  return escapeHtml(trimmed);
};

const formatDateTime = (value: Date): string =>
  new Intl.DateTimeFormat('en-US', {
    dateStyle: 'long',
    timeStyle: 'short',
  }).format(value);

/** Renders one size × count table per Parent NSN. Section is filter-only and is never a grouping. */
const buildParentSection = (group: ParentNsnSizeGroup): string => {
  const nsnLabel = group.nsn ? sanitizeText(group.nsn) : '—';
  const nameLabel = sanitizeText(group.displayName);
  const totalForNsn = group.sizeCounts.reduce((sum, sizeCount) => sum + sizeCount.count, 0);

  const rows =
    group.sizeCounts.length > 0
      ? group.sizeCounts
          .map(
            (sizeCount) => `
        <tr>
          <td>${sanitizeText(sizeCount.size)}</td>
          <td class="numeric">${sizeCount.count}</td>
        </tr>
      `,
          )
          .join('')
      : `
        <tr>
          <td colspan="2" class="empty-row">No sizing records found for this NSN.</td>
        </tr>
      `;

  return `
    <section class="nsn-group">
      <h2 class="nsn-heading">NSN ${nsnLabel} &mdash; ${nameLabel}</h2>
      <table>
        <thead>
          <tr>
            <th>Size</th>
            <th class="numeric">Count</th>
          </tr>
        </thead>
        <tbody>
          ${rows}
        </tbody>
        <tfoot>
          <tr class="totals-row">
            <td class="totals-label">Total for this NSN</td>
            <td class="numeric">${totalForNsn}</td>
          </tr>
        </tfoot>
      </table>
    </section>
  `;
};

export const buildTotalClothingSizeByWorkforceCategoryHtml = ({
  generatedAt,
  workforceCategory,
  section,
  groups,
}: TotalClothingSizeByWorkforceCategoryTemplateOptions): string => {
  const generatedLabel = formatDateTime(generatedAt);
  const workforceCategoryLabel = sanitizeText(workforceCategory);
  const sectionLabel = section ? sanitizeText(section) : null;

  const bodyContent =
    groups.length > 0
      ? groups.map(buildParentSection).join('')
      : `<p class="empty-state">No sizing records found for the selected Workforce Category${
          sectionLabel ? ' and Section' : ''
        }.</p>`;

  return `<!DOCTYPE html>
<html lang="en">
  <head>
    <meta charset="utf-8" />
    <title>Total Clothing Size by Workforce Category &ndash; ${workforceCategoryLabel}</title>
    <style>
      :root {
        color-scheme: light;
      }

      * {
        box-sizing: border-box;
      }

      body {
        margin: 16px;
        font-family: 'Roboto', 'Helvetica Neue', Arial, sans-serif;
        color: #15263c;
        background: #ffffff;
      }

      header {
        margin-bottom: 24px;
      }

      .brand {
        font-size: 14px;
        letter-spacing: 0.08em;
        text-transform: uppercase;
        color: #3a82f6;
        font-weight: 600;
      }

      .title {
        font-size: 28px;
        font-weight: 700;
        margin: 0;
      }

      .generated {
        margin-top: 4px;
        font-size: 14px;
        color: #4a5c72;
      }

      .meta-grid {
        display: grid;
        grid-template-columns: repeat(auto-fit, minmax(220px, 1fr));
        gap: 16px;
        margin-bottom: 24px;
      }

      .meta-block {
        padding: 12px 16px;
        border: 1px solid #e1e8f5;
        border-radius: 8px;
        background: #f8fbff;
      }

      .meta-label {
        display: block;
        font-size: 12px;
        font-weight: 600;
        letter-spacing: 0.06em;
        text-transform: uppercase;
        color: #5c6f85;
        margin-bottom: 4px;
      }

      .meta-value {
        font-size: 16px;
        font-weight: 600;
        color: #15263c;
      }

      .nsn-group {
        margin-bottom: 24px;
      }

      .nsn-heading {
        font-size: 18px;
        font-weight: 700;
        margin: 0 0 8px;
        color: #15263c;
      }

      table {
        width: 100%;
        border-collapse: collapse;
        border: 1px solid #d7e3f4;
      }

      thead th {
        background: #e9f1ff;
        text-align: left;
        font-size: 12px;
        letter-spacing: 0.05em;
        text-transform: uppercase;
        padding: 10px 12px;
        color: #4a5c72;
        border-bottom: 1px solid #d7e3f4;
      }

      tbody td {
        padding: 12px;
        border-bottom: 1px solid #edf3fc;
        font-size: 14px;
        color: #1f2f45;
      }

      tbody tr:last-child td {
        border-bottom: none;
      }

      tbody tr:nth-child(even) td {
        background: #f9fbff;
      }

      .numeric {
        text-align: right;
        font-variant-numeric: tabular-nums;
      }

      .empty-row {
        text-align: center;
        font-style: italic;
        color: #6b7c92;
        padding: 24px 12px;
      }

      .empty-state {
        font-style: italic;
        color: #6b7c92;
      }

      .totals-row td {
        background: #e9f1ff;
        font-weight: 700;
        border-top: 1px solid #d7e3f4;
      }

      .totals-label {
        text-align: right;
      }

      footer {
        margin-top: 32px;
        font-size: 12px;
        color: #5c6f85;
        text-align: center;
      }

      @media print {
        body {
          margin: 8mm;
        }
      }
    </style>
  </head>
  <body>
    <header>
      <div class="brand">eQuip Transaction Suite</div>
      <h1 class="title">Total Clothing Size by Workforce Category</h1>
      <div class="generated">Generated ${escapeHtml(generatedLabel)}</div>
    </header>

    <section class="meta-grid">
      <div class="meta-block">
        <span class="meta-label">Workforce Category</span>
        <span class="meta-value">${workforceCategoryLabel}</span>
      </div>
      ${
        sectionLabel
          ? `
      <div class="meta-block">
        <span class="meta-label">Section</span>
        <span class="meta-value">${sectionLabel}</span>
      </div>`
          : ''
      }
    </section>

    ${bodyContent}

    <footer>
      Generated ${escapeHtml(generatedLabel)} &middot; ${getReportFooterText()}
    </footer>
  </body>
</html>`;
};
